import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polygon,
  Popup,
  useMap,
  ZoomControl,
  Tooltip,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-cluster/lib/assets/MarkerCluster.css";
import "react-leaflet-cluster/lib/assets/MarkerCluster.Default.css";
import styled from "styled-components";
import Title from "antd/es/typography/Title";
import { Button } from "antd";
import { fetchProjectDetails } from "../../store/slices/projectSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// Fix for Leaflet icons not showing properly in React
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom styled components
const MapContainerWrapper = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 16px;
  border: 2px solid #42005a;
`;

const MapTitle = styled.h2`
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const MapWrapper = styled.div`
  height: 500px; /* Increased height for better visibility */
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;

const EmptyMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 500px;
  color: gray;
  font-size: 14px;
`;

const Legend = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: help;
  position: relative;
`;

const LegendBox = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${(props) => props.color || "gray"};
`;

const StatusFilter = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ active: boolean; color: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid ${(props) => (props.active ? props.color : "#ddd")};
  background-color: ${(props) => (props.active ? `${props.color}20` : "white")};
  color: ${(props) => (props.active ? props.color : "#666")};
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) =>
      props.active ? `${props.color}30` : "#f5f5f5"};
  }
`;

const ZoomHint = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(255, 255, 255, 0.9);
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoTooltip = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  max-width: 200px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 1000;

  ${LegendItem}:hover & {
    opacity: 1;
  }
`;

// Calculate status based on is_visited, is_updated, and suivi_initial
const getTrancheStatus = (tranche: any) => {
  // Check for suivi_initial first
  if (tranche.suivi_initial === false) {
    return "no_initial_follow";
  } else if (!tranche.is_visited) {
    return "not_visited";
  } else if (tranche.is_visited && tranche.is_updated) {
    return "updated";
  } else {
    return "needs_update";
  }
};

// Custom marker icon based on status
const createColoredIcon = (status: string) => {
  const colors: Record<string, string> = {
    updated: "#4CAF50", // Green
    not_visited: "#F44336", // Red
    needs_update: "#FF9800", // Orange
    no_initial_follow: "#9E9E9E", // Gray for no initial follow-up
  };

  const emojis: Record<string, string> = {
    updated: "✓",
    not_visited: "✗",
    needs_update: "⟳",
    no_initial_follow: "◯",
  };

  return L.divIcon({
    className: "custom-marker-icon",
    html: `<div style="background-color: ${
      colors[status] || "#2196F3"
    }; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; display: flex; justify-content: center; align-items: center; color: white; font-size: 12px; font-weight: bold;">${
      emojis[status] || "•"
    }</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
};

// Helper component to handle zoom-based polygon visibility
interface ZoomHandlerProps {
  setShowPolygons: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentZoom: React.Dispatch<React.SetStateAction<number>>;
}
const ZoomHandler: React.FC<ZoomHandlerProps> = ({
  setShowPolygons,
  setCurrentZoom,
}) => {
  const map = useMap();

  useEffect(() => {
    const handleZoomend = () => {
      const currentZoom = map.getZoom();
      setCurrentZoom(currentZoom);
      // Increase this threshold to require more zoom for polygons
      setShowPolygons(currentZoom >= 16); // Changed from 14 to 16
    };

    // Initialize
    handleZoomend();

    map.on("zoomend", handleZoomend);
    return () => {
      map.off("zoomend", handleZoomend);
    };
  }, [map, setShowPolygons, setCurrentZoom]);

  return null;
};

// Get status color and emoji
const getStatusInfo = (status: string) => {
  const statusMap: Record<
    string,
    { color: string; emoji: string; label: string }
  > = {
    updated: { color: "#4CAF50", emoji: "✓", label: "Actualisé (à jour)" },
    not_visited: {
      color: "#F44336",
      emoji: "✗",
      label: "Non visité (données manquantes)",
    },
    needs_update: {
      color: "#FF9800",
      emoji: "⟳",
      label: "À actualiser (données périmées)",
    },
    no_initial_follow: {
      color: "#9E9E9E",
      emoji: "◯",
      label: "Sans suivi initial",
    },
  };
  return statusMap[status] || { color: "#2196F3", emoji: "•", label: status };
};

// Custom Marker component that handles hover and click
interface CustomMarkerProps {
  tranche: any;
  statusInfo: any;
  status: string;
  setSelectedTranche: (tranche: any) => void;
}

const CustomMarker: React.FC<CustomMarkerProps> = ({
  tranche,
  statusInfo,
  status,
  setSelectedTranche,
}) => {
  const map = useMap();
  const popupContentRef = useRef<HTMLDivElement>(null);

  const handleMarkerClick = () => {
    // When marker is clicked, set selected tranche and zoom in
    setSelectedTranche(tranche);

    // Zoom in to the specific location
    map.flyTo(
      [tranche.coordinates.latitude, tranche.coordinates.longitude],
      17, // Zoom level for displaying polygons
      {
        animate: true,
        duration: 0.5,
      }
    );
  };

  return (
    <Marker
      position={[tranche.coordinates.latitude, tranche.coordinates.longitude]}
      icon={createColoredIcon(status)}
      eventHandlers={{
        click: handleMarkerClick,
      }}
    >
      {/* Tooltip is shown on hover */}
      <Tooltip permanent={false} direction="top" opacity={1} interactive={true}>
        <div style={{ minWidth: "200px" }} ref={popupContentRef}>
          <h3 style={{ margin: "0 0 8px 0", fontSize: "16px" }}>
            {tranche.display_name}
          </h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "6px",
            }}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: statusInfo.color,
              }}
            />
            <span
              style={{
                color: statusInfo.color,
                fontWeight: "bold",
                fontSize: "13px",
              }}
            >
              {statusInfo.label}
            </span>
          </div>
          <p style={{ margin: "4px 0", fontSize: "13px" }}>
            <strong>Type:</strong> {tranche.type_realisation}
          </p>
          {tranche.date_visite && (
            <p style={{ margin: "4px 0", fontSize: "13px" }}>
              <strong>Dernière visite:</strong>{" "}
              {new Date(tranche.date_visite).toLocaleDateString()}
            </p>
          )}
          {status === "no_initial_follow" && (
            <p style={{ margin: "4px 0", fontSize: "13px", color: "#9E9E9E" }}>
              <strong>Suivi initial:</strong> Non
            </p>
          )}
        </div>
      </Tooltip>
    </Marker>
  );
};

interface Props {
  tranches: any[];
}

// Custom cluster group component
const EnhancedClusterGroup = ({ children }: any) => {
  const map = useMap();

  // Custom icon creation function
  const createClusterIcon = (cluster: any) => {
    const childCount = cluster.getChildCount();
    const size = childCount < 10 ? 30 : childCount < 50 ? 40 : 50;

    return L.divIcon({
      html: `<div style="background-color:#42005a; 
                          color:white;
                          border:2px solid white;
                          border-radius:50%;
                          width:${size}px;
                          height:${size}px;
                          display:flex;
                          align-items:center;
                          justify-content:center;
                          font-weight:bold;
                          box-shadow:0 0 6px rgba(0,0,0,0.3);
                          transition:transform 0.2s;">
                 ${childCount}
               </div>`,
      className: "custom-cluster-icon",
      iconSize: L.point(size, size, true),
    });
  };

  return (
    <MarkerClusterGroup
      chunkedLoading
      maxClusterRadius={60}
      spiderfyDistanceMultiplier={1.5}
      disableClusteringAtZoom={15}
      zoomToBoundsOnClick={true}
      spiderfyOnMaxZoom={false} // We'll handle this manually
      showCoverageOnHover={false}
      iconCreateFunction={createClusterIcon}
      // Custom event handlers
      onClusterClick={(cluster: any) => {
        const currentZoom = map.getZoom();
        const maxClusterZoom = 14;

        if (currentZoom >= maxClusterZoom) {
          // If we're at high zoom, spiderfy the cluster
          cluster.spiderfy();
        } else {
          // Otherwise zoom to cluster bounds
          map.flyToBounds(cluster.getBounds(), {
            padding: [50, 50],
            duration: 0.5,
          });
        }
      }}
    >
      {children}
    </MarkerClusterGroup>
  );
};

const TranchesMap: React.FC<Props> = ({ tranches }) => {
  const [showPolygons, setShowPolygons] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(6);
  const [selectedTranche, setSelectedTranche] = useState<any>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, boolean>>({
    updated: true,
    not_visited: true,
    needs_update: true,
    no_initial_follow: true,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const defaultCenter: [number, number] = [
    33.59056165004032, -7.650668151296751,
  ];

  // Add status to each tranche
  const tranchesWithStatus =
    tranches?.map((tranche) => ({
      ...tranche,
      status: getTrancheStatus(tranche),
    })) || [];

  // Filter tranches based on active status filters
  const filteredTranches = tranchesWithStatus.filter(
    (tranche) => activeFilters[tranche.status] !== false
  );

  const toggleFilter = (status: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  if (!tranches || tranches.length === 0) {
    return (
      <MapContainerWrapper>
        <EmptyMessage>Aucune donnée disponible à localiser</EmptyMessage>
      </MapContainerWrapper>
    );
  }

  // Calculate counts for each status
  const statusCounts = tranchesWithStatus.reduce((acc, tranche) => {
    const status = tranche.status;
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <MapContainerWrapper>
      <MapTitle>
        <span style={{ fontSize: "12px", fontWeight: "normal" }}>
          {filteredTranches.length} sur {tranches.length} tranches affichés
        </span>
      </MapTitle>

      <StatusFilter>
        {Object.keys(activeFilters).map((status) => {
          const info = getStatusInfo(status);
          return (
            <FilterButton
              key={status}
              active={activeFilters[status]}
              color={info.color}
              onClick={() => toggleFilter(status)}
            >
              <span>{info.emoji}</span>
              {info.label} ({statusCounts[status] || 0})
            </FilterButton>
          );
        })}
      </StatusFilter>

      <MapWrapper>
        <MapContainer
          center={defaultCenter}
          zoom={currentZoom}
          style={{ height: "100%", width: "100%" }}
          maxZoom={18}
          zoomControl={false} // We'll add our own zoom control
        >
          <ZoomControl position="topright" />
          {/* Base satellite layer */}
          <TileLayer
            attribution="Google Maps"
            url="https://www.google.cn/maps/vt?lyrs=s@189&gl=cn&x={x}&y={y}&z={z}"
          />
          {/* Transparent roads-only layer */}
          <TileLayer
            attribution="OpenStreetMap/Carto"
            url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
          />

          <ZoomHandler
            setShowPolygons={setShowPolygons}
            setCurrentZoom={setCurrentZoom}
          />

          {currentZoom < 16 && (
            <ZoomHint>
              <span>Zoom in pour voir les contours détaillés des projets</span>
            </ZoomHint>
          )}

          <EnhancedClusterGroup>
            {filteredTranches.map((tranche) => {
              const statusInfo = getStatusInfo(tranche.status);

              return (
                <React.Fragment key={tranche.id}>
                  {!showPolygons && (
                    <CustomMarker
                      tranche={tranche}
                      statusInfo={statusInfo}
                      status={tranche.status}
                      setSelectedTranche={setSelectedTranche}
                    />
                  )}

                  {showPolygons && tranche.coordinates.polygon && (
                    <Polygon
                      positions={tranche.coordinates.polygon}
                      pathOptions={{
                        fillColor: statusInfo.color,
                        weight: 2,
                        opacity: 1,
                        color: "white",
                        fillOpacity: 0.7,
                      }}
                    >
                      <Popup>
                        <div style={{ minWidth: "200px" }}>
                          <h3 style={{ margin: "0 0 8px 0", fontSize: "16px" }}>
                            {tranche.display_name}
                          </h3>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              marginBottom: "6px",
                            }}
                          >
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                backgroundColor: statusInfo.color,
                              }}
                            />
                            <span
                              style={{
                                color: statusInfo.color,
                                fontWeight: "bold",
                                fontSize: "13px",
                              }}
                            >
                              {statusInfo.label}
                            </span>
                          </div>
                          <p style={{ margin: "4px 0", fontSize: "13px" }}>
                            <strong>Type:</strong> {tranche.type_realisation}
                          </p>
                          {tranche.date_visite && (
                            <p style={{ margin: "4px 0", fontSize: "13px" }}>
                              <strong>Dernière visite:</strong>{" "}
                              {new Date(
                                tranche.date_visite
                              ).toLocaleDateString()}
                            </p>
                          )}
                          {tranche.status === "no_initial_follow" && (
                            <p
                              style={{
                                margin: "4px 0",
                                fontSize: "13px",
                                color: "#9E9E9E",
                              }}
                            >
                              <strong>Suivi initial:</strong> Non
                            </p>
                          )}

                          <div
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              type="primary"
                              onClick={async () => {
                                await dispatch(
                                  fetchProjectDetails(tranche.project_id)
                                );
                                navigate("/projectDetails");
                              }}
                            >
                              Voir les détails{" "}
                            </Button>
                          </div>
                        </div>
                      </Popup>
                    </Polygon>
                  )}
                </React.Fragment>
              );
            })}
          </EnhancedClusterGroup>
        </MapContainer>
      </MapWrapper>

      <Legend>
        {Object.keys(activeFilters).map((status) => {
          const info = getStatusInfo(status);
          return (
            <LegendItem key={status}>
              <LegendBox color={info.color} />
              <span>{info.label}</span>
              <InfoTooltip>
                {statusCounts[status] || 0} projet(s) dans cette catégorie
              </InfoTooltip>
            </LegendItem>
          );
        })}
      </Legend>
    </MapContainerWrapper>
  );
};

export default TranchesMap;
